import {
  DEFAULT_ORGX_BASE_URL,
  OPENCLAW_CONFIG_PATH,
  ORGX_WIZARD_AUTH_PATH,
} from "../constants.js";
import { readOpenClawAuth } from "../surfaces/openclaw-config.js";
import { readTextIfExists } from "./fs.js";
import {
  extractKeyPrefix,
  readWizardAuth,
  readWizardAuthMetadata,
} from "./auth-store.js";
import { fetchWithRetry } from "./network.js";

export type OrgxAuthSource =
  | "environment"
  | "wizard-store"
  | "openclaw-config-file";

export interface ResolvedOrgxAuth {
  apiKey: string;
  keyPrefix: string;
  baseUrl: string;
  source: OrgxAuthSource;
  path?: string;
  verifiedAt?: string;
}

export interface OrgxAuthCheckResult {
  configured: boolean;
  ok: boolean;
  skipped: boolean;
  source: OrgxAuthSource | "none";
  keyPrefix?: string;
  baseUrl?: string;
  path?: string;
  verifiedAt?: string;
  status?: number;
  url?: string;
  error?: string;
  data?: unknown;
}

export interface ResolveOrgxAuthOptions {
  authPath?: string;
  openclawConfigPath?: string;
}

export interface OrgxAuthHint {
  keyPrefix: string;
  baseUrl: string;
  source: OrgxAuthSource;
  path?: string;
  verifiedAt?: string;
}

function normalizeHost(value: string): string {
  return value.trim().toLowerCase().replace(/^\[|\]$/g, "");
}

function isLoopbackHostname(hostname: string): boolean {
  const normalized = normalizeHost(hostname);
  return normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1";
}

export function normalizeOrgxBaseUrl(raw?: string): string {
  const candidate = raw?.trim() || DEFAULT_ORGX_BASE_URL;

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return DEFAULT_ORGX_BASE_URL;
    }

    if (parsed.username || parsed.password) {
      return DEFAULT_ORGX_BASE_URL;
    }

    if (parsed.protocol === "http:" && !isLoopbackHostname(parsed.hostname)) {
      return DEFAULT_ORGX_BASE_URL;
    }

    parsed.search = "";
    parsed.hash = "";
    parsed.pathname = parsed.pathname.replace(/\/api\/?$/, "").replace(/\/+$/, "");
    return parsed.toString().replace(/\/+$/, "");
  } catch {
    return DEFAULT_ORGX_BASE_URL;
  }
}

export function buildOrgxApiUrl(path: string, baseUrl?: string): string {
  const parsedBase = new URL(normalizeOrgxBaseUrl(baseUrl));
  const normalizedBase = parsedBase.toString().replace(/\/+$/, "");
  const apiBase = normalizedBase.endsWith("/api") ? normalizedBase : `${normalizedBase}/api`;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${apiBase}${normalizedPath}`;
}

function withResolvedBaseUrl(baseUrl: string, envBaseUrl?: string): string {
  return normalizeOrgxBaseUrl(envBaseUrl?.trim() || baseUrl);
}

function buildResolvedAuth(
  apiKey: string,
  baseUrl: string,
  source: OrgxAuthSource,
  extras: Pick<ResolvedOrgxAuth, "path" | "verifiedAt"> = {},
): ResolvedOrgxAuth {
  return {
    apiKey: apiKey.trim(),
    keyPrefix: extractKeyPrefix(apiKey),
    baseUrl,
    source,
    ...(extras.path ? { path: extras.path } : {}),
    ...(extras.verifiedAt ? { verifiedAt: extras.verifiedAt } : {}),
  };
}

function buildAuthHint(
  keyPrefix: string,
  baseUrl: string,
  source: OrgxAuthSource,
  extras: Pick<OrgxAuthHint, "path" | "verifiedAt"> = {},
): OrgxAuthHint {
  return {
    keyPrefix: keyPrefix.trim(),
    baseUrl,
    source,
    ...(extras.path ? { path: extras.path } : {}),
    ...(extras.verifiedAt ? { verifiedAt: extras.verifiedAt } : {}),
  };
}

export function peekResolvedOrgxAuth(
  options: ResolveOrgxAuthOptions = {},
): OrgxAuthHint | null {
  const envApiKey = process.env.ORGX_API_KEY?.trim();
  const envBaseUrl = process.env.ORGX_BASE_URL?.trim();
  if (envApiKey) {
    return buildAuthHint(
      extractKeyPrefix(envApiKey),
      withResolvedBaseUrl(DEFAULT_ORGX_BASE_URL, envBaseUrl),
      "environment",
    );
  }

  const authPath = options.authPath ?? ORGX_WIZARD_AUTH_PATH;
  const stored = readWizardAuthMetadata(authPath);
  if (stored) {
    return buildAuthHint(
      stored.keyPrefix,
      withResolvedBaseUrl(stored.baseUrl, envBaseUrl),
      "wizard-store",
      {
        path: authPath,
        verifiedAt: stored.verifiedAt,
      },
    );
  }

  const openclawConfigPath = options.openclawConfigPath ?? OPENCLAW_CONFIG_PATH;
  const openclawAuth = readOpenClawAuth(
    openclawConfigPath ? readTextIfExists(openclawConfigPath) : null,
  );
  if (openclawAuth.apiKey && openclawConfigPath) {
    return buildAuthHint(
      extractKeyPrefix(openclawAuth.apiKey),
      withResolvedBaseUrl(openclawAuth.baseUrl || DEFAULT_ORGX_BASE_URL, envBaseUrl),
      "openclaw-config-file",
      { path: openclawConfigPath },
    );
  }

  return null;
}

export async function resolveOrgxAuth(
  options: ResolveOrgxAuthOptions = {},
): Promise<ResolvedOrgxAuth | null> {
  const envApiKey = process.env.ORGX_API_KEY?.trim();
  const envBaseUrl = process.env.ORGX_BASE_URL?.trim();
  if (envApiKey) {
    return buildResolvedAuth(
      envApiKey,
      withResolvedBaseUrl(DEFAULT_ORGX_BASE_URL, envBaseUrl),
      "environment",
    );
  }

  const authPath = options.authPath ?? ORGX_WIZARD_AUTH_PATH;
  const stored = await readWizardAuth(authPath);
  if (stored) {
    return buildResolvedAuth(
      stored.apiKey,
      withResolvedBaseUrl(stored.baseUrl, envBaseUrl),
      "wizard-store",
      {
        path: authPath,
        verifiedAt: stored.verifiedAt,
      },
    );
  }

  const openclawConfigPath = options.openclawConfigPath ?? OPENCLAW_CONFIG_PATH;
  const openclawAuth = readOpenClawAuth(
    openclawConfigPath ? readTextIfExists(openclawConfigPath) : null,
  );
  if (openclawAuth.apiKey && openclawConfigPath) {
    return buildResolvedAuth(
      openclawAuth.apiKey,
      withResolvedBaseUrl(openclawAuth.baseUrl || DEFAULT_ORGX_BASE_URL, envBaseUrl),
      "openclaw-config-file",
      { path: openclawConfigPath },
    );
  }

  return null;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function verifyOrgxAuth(
  auth: Pick<ResolvedOrgxAuth, "apiKey" | "baseUrl" | "source" | "keyPrefix"> & {
    path?: string;
    verifiedAt?: string;
  },
): Promise<OrgxAuthCheckResult> {
  const url = buildOrgxApiUrl("/client/sync", auth.baseUrl);

  try {
    const response = await fetchWithRetry(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth.apiKey}`,
        "Content-Type": "application/json",
      },
      body: "{}",
    });

    const data = await parseResponseBody(response);

    return {
      configured: true,
      ok: response.ok,
      skipped: false,
      source: auth.source,
      keyPrefix: auth.keyPrefix,
      baseUrl: auth.baseUrl,
      ...(auth.path ? { path: auth.path } : {}),
      ...(auth.verifiedAt ? { verifiedAt: auth.verifiedAt } : {}),
      status: response.status,
      url,
      data,
      ...(response.ok ? {} : { error: `HTTP ${response.status}` }),
    };
  } catch (error) {
    return {
      configured: true,
      ok: false,
      skipped: false,
      source: auth.source,
      keyPrefix: auth.keyPrefix,
      baseUrl: auth.baseUrl,
      ...(auth.path ? { path: auth.path } : {}),
      ...(auth.verifiedAt ? { verifiedAt: auth.verifiedAt } : {}),
      url,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export async function checkOrgxAuth(
  options: ResolveOrgxAuthOptions = {},
): Promise<OrgxAuthCheckResult> {
  const auth = await resolveOrgxAuth(options);
  if (!auth) {
    return {
      configured: false,
      ok: false,
      skipped: true,
      source: "none",
      error: "No OrgX account connected. Run `orgx-wizard auth login` to connect.",
      url: buildOrgxApiUrl("/client/sync", DEFAULT_ORGX_BASE_URL),
    };
  }

  return verifyOrgxAuth(auth);
}

export function describeSetupStatusScope(): string {
  return "The setup-status endpoint is still service-key-only and uses ORGX_SERVICE_KEY.";
}
